import { UUIDHelper, ViewModelUtils } from '@nts/std/utility';
import { BehaviorSubject, Subject } from 'rxjs';
import { BaseErrorInterface, BaseViewModelInterface, HasViewModelEventDispatcherInterface, MessageContainerInterface } from '@nts/std/interfaces';
import { MessageContainer } from './message-container';
import { SourceMessage } from '@nts/std/types';

export abstract class BaseViewModel implements BaseViewModelInterface {

    protected internalIsRequired = true;
    protected _isEnabled = true;
    protected _isVisible = true;
    #skipValidation = false;

    // Necessario per identificare univocamente un view model (vedi gestione errori)
    readonly uniqueId = UUIDHelper.generateUUID();
    onErrorStatusChanged: Subject<void> = new Subject();
    onFocusRequested: Subject<void> = new Subject();

    isEnabled$ = new BehaviorSubject<boolean>(this._isEnabled);
    isRequired$ = new BehaviorSubject<boolean>(this.internalIsRequired);
    isVisible$ = new BehaviorSubject<boolean>(this._isVisible);
    skipValidation$ = new BehaviorSubject<boolean>(this.#skipValidation);

    destroySubscribers$: Subject<void> = new Subject();
    errors$: BehaviorSubject<Array<string>> = new BehaviorSubject<Array<string>>([]);

    protected internalParent: BaseViewModelInterface = null;

    get parent() {
        return this.internalParent;
    }
    set parent(value: BaseViewModelInterface) {
        this.internalParent = value;
    }

    get isVisible() {
        return this._isVisible;
    }
    set isVisible(value: boolean) {
        this._isVisible = value;
        this.isVisible$.next(value);
    }

    get isRequired() {
        return this.internalIsRequired;
    }
    set isRequired(value: boolean) {
        this.internalIsRequired = value;
        this.isRequired$.next(value);
    }

    get hasErrors(): boolean {
        return false;
    }

    getErrors(): Array<string> {
        return this.errors$.value;
    }

    get hasDecodeError() {
        return false;
    }

    get isEnabled() {
        return this._isEnabled;
    }
    set isEnabled(value) {
        this._isEnabled = value;
        this.isEnabled$.next(value);
    }

    get skipValidation() {
        return this.#skipValidation;
    }
    set skipValidation(value) {
        this.#skipValidation = value;
        this.skipValidation$.next(value);
    }

    get messageContainerCollection(): Array<MessageContainerInterface> {
        return this._messageContainerCollection;
    }

    private _messageContainerCollection = new Array<MessageContainerInterface>();

    onDestroy() {
        if (this._messageContainerCollection?.length > 0) {
            this._messageContainerCollection = [];
            if (this.hasEventDispatcher()) {
                this.eventDispatcher.onClearMessagesInViewModel.next(this);
            }
        }
        this.destroySubscribers$?.next();

        this.destroySubscribers$?.complete();


        // disabillitato destroy per evitare che si rompino i MS della versione REL_11_0_X, dalla versione successiva è abilitato
        return;

        this.destroySubscribers$ = null;

        this.onErrorStatusChanged?.complete();
        this.onErrorStatusChanged = null;

        this.onFocusRequested?.complete();
        this.onFocusRequested = null;

        this.isEnabled$?.complete();
        this.isEnabled$ = null;

        this.isRequired$?.complete();
        this.isRequired$ = null;

        this.isVisible$?.complete();
        this.isVisible$ = null;

        this.skipValidation$?.complete();
        this.skipValidation$ = null;

        this.errors$?.complete();
        this.errors$ = null;

        this.internalParent = null;
    }

    hasEventDispatcher(): this is HasViewModelEventDispatcherInterface {
        return ViewModelUtils.hasEventDispatcher(this);
    }

    clearErrors() {

    }

    validateProperty(propertyName: string): void {

    }

    validate() {

    }

    addError(
        sourceMessage: SourceMessage,
        error: BaseErrorInterface,
        notifyDispatcher = true
    ) {
        let add = true;
        this.messageContainerCollection.forEach((mc) => {
            if (mc.contains(error)) {
                add = false;
            }
        });
        if (add) {
            this.messageContainerCollection.splice(0, 0, new MessageContainer(error, sourceMessage, this.uniqueId));
            if (this.hasEventDispatcher() && notifyDispatcher) {
                this.eventDispatcher.onAddMessageInViewModel.next({ viewModel: this, messages: [error] });
            }
        }
        this.updateCurrentErrors();
        this.onErrorStatusChanged.next();
    }

    // TODO Tommy verificare se devo lanciare l'evento per aggiornare i servizi
    removeError(item: MessageContainerInterface) {
        this.messageContainerCollection.splice(this.messageContainerCollection.indexOf(item), 1);
        if (this.hasEventDispatcher()) {
            this.eventDispatcher.onRemovedMessageInViewModel.next(this);
        }
        // this.propertyViewModelChanged.emit();
        this.updateCurrentErrors();
        this.onErrorStatusChanged.next();
    }

    removeMessage(item: MessageContainerInterface) {
        this.messageContainerCollection.splice(this.messageContainerCollection.indexOf(item), 1);
        if (this.hasEventDispatcher()) {
            this.eventDispatcher.onRemovedMessageInViewModel.next(this);
        }
    }

    protected removeMessagesFromCondition(condition: (msg: MessageContainerInterface) => boolean) {
        const list = this.messageContainerCollection.filter(condition);
        for (const item of list) {
            this.messageContainerCollection.splice(this.messageContainerCollection.indexOf(item), 1);

        }
    }

    protected removeMessagesFromSource(sourceMessage: SourceMessage) {
        let errorsChanged = false;
        const list = this.messageContainerCollection.filter(x => x.sourceMessage === sourceMessage);
        list.forEach((item) => {
            if (item.isErrorMessage) {
                errorsChanged = true;
            }
            this.messageContainerCollection.splice(this.messageContainerCollection.indexOf(item), 1);
        });
        if (this.hasEventDispatcher()) {
            this.eventDispatcher.onRemovedMessageInViewModel.next(this);
        }
        if (errorsChanged) {
            this.updateCurrentErrors();
            this.onErrorStatusChanged.next();
        }
    }

    protected removeAllMessages() {
        this._messageContainerCollection = [];
        if (this.hasEventDispatcher()) {
            this.eventDispatcher.onClearMessagesInViewModel.next(this);
        }
        this.errors$.next([]);
        this.onErrorStatusChanged.next();
    }

    updateCurrentErrors() {
        const errors = this.messageContainerCollection.filter(x => x.isErrorMessage).map(x => x.message);
        this.errors$.next(errors);
    }
}
